// src/components/BCP/BusinessInfoStep.tsx
import React, { useState, useEffect } from 'react';
import { BusinessInfoStepProps, AdditionalLocation } from '../../types/bcpForm';
import { locationService, LocationData } from '../../services/locationService';

const BusinessInfoStep: React.FC<BusinessInfoStepProps> = ({
  additionalLocations,
  onAddAdditionalLocation,
  onUpdateAdditionalLocationField,
  onRemoveAdditionalLocation,
  form,
  setForm,
  handleFieldChange,
  handleFieldBlur,
  shouldShowError,
  getFieldError,
}) => {
  const [usStates, setUsStates] = useState<{ value: string; label: string }[]>([]);
  const [isValidatingLocation, setIsValidatingLocation] = useState(false);
  const [locationValidation, setLocationValidation] = useState<{
    validated: boolean;
    error?: string;
    coordinates?: { lat: number; lng: number };
  } | null>(null);

  // Load US states on component mount
  useEffect(() => {
    const loadStates = async () => {
      try {
        const states = locationService.getUSStates();
        setUsStates(states);
      } catch (error) {
        console.error('Failed to load states:', error);
      }
    };
    loadStates();
  }, []);

  // Validate location when address fields change
  const validateLocation = async () => {
    if (!form.primary_location.city || !form.primary_location.state || !form.primary_location.zip) {
      return;
    }

    setIsValidatingLocation(true);
    try {
      const locationData: LocationData = {
        address: form.primary_location.address,
        city: form.primary_location.city,
        state: form.primary_location.state,
        zip: form.primary_location.zip,
        country: 'USA'
      };

      const result = await locationService.validateLocation(locationData);
      setLocationValidation({
        validated: result.validated || false,
        error: result.error,
        coordinates: result.coordinates
      });
    } catch (error) {
      setLocationValidation({
        validated: false,
        error: 'Location validation failed'
      });
    } finally {
      setIsValidatingLocation(false);
    }
  };

  // Auto-validate location when all required fields are filled
  useEffect(() => {
    if (form.primary_location.city && form.primary_location.state && form.primary_location.zip) {
      const timeoutId = setTimeout(validateLocation, 1000); // Debounce
      return () => clearTimeout(timeoutId);
    }
  }, [form.primary_location.city, form.primary_location.state, form.primary_location.zip]);

  return (
  <div className="space-y-6">
    <div>
      <h2 className="mb-2 text-2xl font-bold text-gray-900">Business Information</h2>
      <p className="text-gray-600">Tell us about your business to get started</p>
    </div>

    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      <div>
        <label className="block mb-2 text-sm font-semibold text-gray-700">
          Business Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          placeholder="Enter your business name"
          value={form.business_name}
          onChange={(e) => handleFieldChange('business_name', e.target.value)}
          onBlur={() => handleFieldBlur('business_name')}
          className="px-4 py-3 w-full rounded-xl border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {shouldShowError('business_name') && <p className="mt-1 text-xs text-red-500">{getFieldError('business_name')}</p>}
      </div>

      <div>
        <label className="block mb-2 text-sm font-semibold text-gray-700">
          Industry <span className="text-red-500">*</span>
        </label>
        <select
          className="px-4 py-3 w-full rounded-xl border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          value={form.industry}
          onChange={(e) => handleFieldChange('industry', e.target.value)}
          onBlur={() => handleFieldBlur('industry')}
        >
          <option>Select your industry</option>
          <option value="Technology">Technology</option>
          <option value="Healthcare">Healthcare</option>
          <option value="Manufacturing">Manufacturing</option>
          <option value="Construction">Construction</option>
          <option value="Transportation">Transportation</option>
          <option value="Energy">Energy</option>
          <option value="Automotive">Automotive</option>
          <option value="Agriculture">Agriculture</option>
          <option value="Retail">Retail</option>
          <option value="Finance">Finance</option>
          <option value="Education">Education</option>
          <option value="Government">Government</option>
          <option value="House of Worship">House of Worship</option>
          <option value="Hospitality">Hospitality</option>
          <option value="Other">Other</option>
        </select>
        {shouldShowError('industry') && <p className="mt-1 text-xs text-red-500">{getFieldError('industry')}</p>}
      </div>

      <div>
        <label className="block mb-2 text-sm font-semibold text-gray-700">
          Business Type <span className="text-red-500">*</span>
        </label>
        <select
          className="px-4 py-3 w-full rounded-xl border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          value={form.business_type}
          onChange={(e) => handleFieldChange('business_type', e.target.value)}
          onBlur={() => handleFieldBlur('business_type')}
        >
          <option>Select business type</option>
          <option>Corporation</option>
          <option>LLC</option>
          <option>Partnership</option>
          <option>Sole Proprietorship</option>
          <option>Non-profit</option>
        </select>
        {shouldShowError('business_type') && <p className="mt-1 text-xs text-red-500">{getFieldError('business_type')}</p>}
      </div>

      <div>
        <label className="block mb-2 text-sm font-semibold text-gray-700">
          Number of Employees <span className="text-red-500">*</span>
        </label>
        <input
          type="number"
          placeholder="Enter number of employees"
          value={form.employee_count || ''}
          onChange={(e) => handleFieldChange('employee_count', e.target.value ? parseInt(e.target.value) : 0)}
          onBlur={() => handleFieldBlur('employee_count')}
          className="px-4 py-3 w-full rounded-xl border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {shouldShowError('employee_count') && <p className="mt-1 text-xs text-red-500">{getFieldError('employee_count')}</p>}
      </div>

      <div className="md:col-span-2">
        <label className="block mb-2 text-sm font-semibold text-gray-700">
          Annual Revenue
        </label>
        <input
          type="text"
          placeholder="Enter annual revenue (e.g., $500K, $1.2M)"
          value={form.annual_revenue}
          onChange={(e) => handleFieldChange('annual_revenue', e.target.value)}
          onBlur={() => handleFieldBlur('annual_revenue')}
          className="px-4 py-3 w-full rounded-xl border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <p className="mt-1 text-xs text-gray-500">Examples: $500K, $1.2M, $750K, $2.5M</p>
        {shouldShowError('annual_revenue') && <p className="mt-1 text-xs text-red-500">{getFieldError('annual_revenue')}</p>}
      </div>

      {/* Primary Location Section */}
      <div className="md:col-span-2">
        <label className="block mb-2 text-sm font-semibold text-gray-700">
          Primary Business Location
        </label>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Location Name</label>
            <input
              type="text"
              placeholder="e.g., Headquarters"
              value={form.primary_location.name}
              onChange={(e) => handleFieldChange('primary_location.name', e.target.value)}
              onBlur={() => handleFieldBlur('primary_location.name')}
              className="px-4 py-2 w-full rounded-lg border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {shouldShowError('primary_location.name') && <p className="mt-1 text-xs text-red-500">{getFieldError('primary_location.name')}</p>}
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Contact</label>
            <input
              type="text"
              placeholder="Contact person or department"
              value={form.primary_location.contact}
              onChange={(e) => handleFieldChange('primary_location.contact', e.target.value)}
              onBlur={() => handleFieldBlur('primary_location.contact')}
              className="px-4 py-2 w-full rounded-lg border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {shouldShowError('primary_location.contact') && <p className="mt-1 text-xs text-red-500">{getFieldError('primary_location.contact')}</p>}
          </div>
          <div className="md:col-span-2">
            <label className="block mb-1 text-sm font-medium text-gray-700">Location Address <span className="text-red-500">*</span></label>
            <textarea
              rows={2}
              placeholder="Street, City"
              value={form.primary_location.address}
              onChange={(e) => handleFieldChange('primary_location.address', e.target.value)}
              onBlur={() => handleFieldBlur('primary_location.address')}
              className="px-4 py-2 w-full rounded-lg border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {shouldShowError('primary_location.address') && <p className="mt-1 text-xs text-red-500">{getFieldError('primary_location.address')}</p>}
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">City <span className="text-red-500">*</span></label>
            <input
              type="text"
              placeholder="e.g., San Francisco"
              value={form.primary_location.city}
              onChange={(e) => handleFieldChange('primary_location.city', e.target.value)}
              onBlur={() => handleFieldBlur('primary_location.city')}
              className="px-4 py-2 w-full rounded-lg border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {shouldShowError('primary_location.city') && <p className="mt-1 text-xs text-red-500">{getFieldError('primary_location.city')}</p>}
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">State <span className="text-red-500">*</span></label>
            <select
              value={form.primary_location.state}
              onChange={(e) => handleFieldChange('primary_location.state', e.target.value)}
              onBlur={() => handleFieldBlur('primary_location.state')}
              className="px-4 py-2 w-full rounded-lg border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select State</option>
              {usStates.map((state) => (
                <option key={state.value} value={state.value}>
                  {state.label}
                </option>
              ))}
            </select>
            {shouldShowError('primary_location.state') && <p className="mt-1 text-xs text-red-500">{getFieldError('primary_location.state')}</p>}
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">ZIP <span className="text-red-500">*</span></label>
            <input
              type="text"
              placeholder="e.g., 94105"
              value={form.primary_location.zip}
              onChange={(e) => handleFieldChange('primary_location.zip', e.target.value)}
              onBlur={() => handleFieldBlur('primary_location.zip')}
              className="px-4 py-2 w-full rounded-lg border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {shouldShowError('primary_location.zip') && <p className="mt-1 text-xs text-red-500">{getFieldError('primary_location.zip')}</p>}
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Phone</label>
            <input
              type="tel"
              placeholder="e.g., (555) 123-4567"
              value={form.primary_location.phone}
              onChange={(e) => handleFieldChange('primary_location.phone', e.target.value)}
              onBlur={() => handleFieldBlur('primary_location.phone')}
              className="px-4 py-2 w-full rounded-lg border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {shouldShowError('primary_location.phone') && <p className="mt-1 text-xs text-red-500">{getFieldError('primary_location.phone')}</p>}
          </div>
        </div>

        {/* Location Validation Feedback */}
        {isValidatingLocation && (
          <div className="flex items-center mt-2 text-sm text-blue-600">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Validating location...
          </div>
        )}

        {locationValidation && !isValidatingLocation && (
          <div className={`mt-2 p-3 rounded-lg ${
            locationValidation.validated
              ? 'bg-green-50 border border-green-200'
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-center">
              {locationValidation.validated ? (
                <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              <span className={`text-sm font-medium ${
                locationValidation.validated ? 'text-green-800' : 'text-red-800'
              }`}>
                {locationValidation.validated
                  ? 'Location validated successfully'
                  : `Location validation failed: ${locationValidation.error}`
                }
              </span>
            </div>
            {locationValidation.coordinates && (
              <p className="mt-1 text-xs text-green-700">
                Coordinates: {locationValidation.coordinates.lat.toFixed(4)}, {locationValidation.coordinates.lng.toFixed(4)}
              </p>
            )}
          </div>
        )}
      </div>
    </div>

    {/* Additional Locations Section */}
    <div className="pt-2">
      <div className="flex items-center mb-3 space-x-2">
        <h3 className="text-lg font-semibold text-gray-900">Additional Locations</h3>
        <span className="px-2 py-0.5 text-xs text-blue-700 bg-blue-100 rounded-full border border-blue-200">Optional</span>
      </div>
      <p className="mb-4 text-sm text-gray-600">If your business operates across multiple locations, add them here.</p>

      {additionalLocations.length === 0 && (
        <button
          type="button"
          onClick={onAddAdditionalLocation}
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-md border border-blue-200 hover:bg-blue-100"
        >
          <svg className="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m6-6H6" />
          </svg>
          Add another location
        </button>
      )}

      <div className="space-y-4">
        {additionalLocations.map((loc, index) => (
          <div key={index} className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center space-x-2">
                <div className="flex justify-center items-center w-6 h-6 bg-blue-600 rounded-md">
                  <span className="text-xs font-bold text-white">{index + 1}</span>
                </div>
                <h4 className="text-sm font-semibold text-gray-900">Location #{index + 1}</h4>
              </div>
              <button
                type="button"
                onClick={() => onRemoveAdditionalLocation(index)}
                className="flex items-center text-sm text-red-600 hover:text-red-700"
                aria-label={`Remove location ${index + 1}`}
              >
                <svg className="mr-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Remove
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">Location Name</label>
                <input
                  type="text"
                  value={loc.name}
                  onChange={(e) => onUpdateAdditionalLocationField(index, 'name', e.target.value)}
                  placeholder="e.g., West Warehouse, Branch Office"
                  className="px-4 py-2 w-full rounded-lg border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">Contact</label>
                <input
                  type="text"
                  value={loc.contact}
                  onChange={(e) => onUpdateAdditionalLocationField(index, 'contact', e.target.value)}
                  placeholder="Contact person or department"
                  className="px-4 py-2 w-full rounded-lg border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block mb-1 text-sm font-medium text-gray-700">Address</label>
                <textarea
                  rows={2}
                  value={loc.address}
                  onChange={(e) => onUpdateAdditionalLocationField(index, 'address', e.target.value)}
                  placeholder="Street, City"
                  className="px-4 py-2 w-full rounded-lg border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">City</label>
                <input
                  type="text"
                  value={loc.city || ''}
                  onChange={(e) => onUpdateAdditionalLocationField(index, 'city', e.target.value)}
                  placeholder="e.g., San Francisco"
                  className="px-4 py-2 w-full rounded-lg border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">State</label>
                <input
                  type="text"
                  value={loc.state}
                  onChange={(e) => onUpdateAdditionalLocationField(index, 'state', e.target.value)}
                  placeholder="e.g., CA"
                  className="px-4 py-2 w-full rounded-lg border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">Zip Code</label>
                <input
                  type="text"
                  value={loc.zip}
                  onChange={(e) => onUpdateAdditionalLocationField(index, 'zip', e.target.value)}
                  placeholder="e.g., 94105"
                  className="px-4 py-2 w-full rounded-lg border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  value={loc.phone}
                  onChange={(e) => onUpdateAdditionalLocationField(index, 'phone', e.target.value)}
                  placeholder="e.g., (555) 123-4567"
                  className="px-4 py-2 w-full rounded-lg border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {additionalLocations.length > 0 && (
        <div className="mt-3">
          <button
            type="button"
            onClick={onAddAdditionalLocation}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-md border border-blue-200 hover:bg-blue-100"
          >
            <svg className="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m6-6H6" />
            </svg>
            Add another location
          </button>
        </div>
      )}
    </div>
  </div>
  );
};

export default BusinessInfoStep;